import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js/min';

export const DEFAULT_PHONE_COUNTRY_CODE = 'IN';
export const MAX_PHONE_NATIONAL_DIGITS = 15;

const COUNTRY_NAME_DISPLAY =
  typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

function countryCodeToFlag(countryCode) {
  return String(countryCode || '')
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

function buildPhoneCountryOptions() {
  return getCountries()
    .map((countryCode) => {
      let dialCode = '';
      try {
        dialCode = `+${getCountryCallingCode(countryCode)}`;
      } catch {
        dialCode = '';
      }
      if (!dialCode) return null;
      return {
        code: countryCode,
        name: COUNTRY_NAME_DISPLAY?.of(countryCode) || countryCode,
        dialCode,
        flag: countryCodeToFlag(countryCode),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name) || a.dialCode.localeCompare(b.dialCode));
}

export const PHONE_COUNTRY_OPTIONS = buildPhoneCountryOptions();

export const DEFAULT_PHONE_COUNTRY =
  PHONE_COUNTRY_OPTIONS.find((option) => option.code === DEFAULT_PHONE_COUNTRY_CODE) ||
  PHONE_COUNTRY_OPTIONS[0] || {
    code: DEFAULT_PHONE_COUNTRY_CODE,
    name: 'India',
    dialCode: '+91',
    flag: '🇮🇳',
  };

export function getPhoneCountryOption(countryCode) {
  const normalizedCountryCode = String(countryCode || '').trim().toUpperCase();
  if (!normalizedCountryCode) return DEFAULT_PHONE_COUNTRY;
  return (
    PHONE_COUNTRY_OPTIONS.find((option) => option.code === normalizedCountryCode) ||
    DEFAULT_PHONE_COUNTRY
  );
}

export function validateNationalPhoneByCountry(countryCode, nationalPhone) {
  const selectedCountry = getPhoneCountryOption(countryCode);
  const phoneDigits = String(nationalPhone || '').replace(/\D/g, '');
  if (!phoneDigits) return 'Phone number is required.';

  const e164Phone = `${selectedCountry.dialCode}${phoneDigits}`;
  const lengthError = validatePhoneNumberLength(e164Phone, selectedCountry.code);
  if (lengthError === 'TOO_SHORT') {
    return `Phone number is too short for ${selectedCountry.name}.`;
  }
  if (lengthError === 'TOO_LONG') {
    return `Phone number is too long for ${selectedCountry.name}.`;
  }
  if (lengthError) {
    return `Please enter a valid phone number for ${selectedCountry.name}.`;
  }

  const parsedPhone = parsePhoneNumberFromString(e164Phone, selectedCountry.code);
  if (!parsedPhone || !parsedPhone.isValid()) {
    return `Please enter a valid phone number for ${selectedCountry.name}.`;
  }
  return '';
}

export function buildPhoneForApi(countryCode, nationalPhone) {
  const selectedCountry = getPhoneCountryOption(countryCode);
  const dialDigits = String(selectedCountry.dialCode || '').replace(/\D/g, '');
  const nationalDigits = String(nationalPhone || '').replace(/\D/g, '');
  if (!nationalDigits) return '';
  return `${dialDigits}${nationalDigits}`;
}

export function getPhoneCountryOptionLabel(option) {
  if (!option) return '';
  return `${option.flag} ${option.name} ${option.dialCode}`;
}

export function normalizePhoneInput(value) {
  return String(value || '').replace(/\D/g, '').slice(0, MAX_PHONE_NATIONAL_DIGITS);
}
